// A slim strip at the bottom of the hero band: the check locations as dim dots
// at their real longitude and latitude, and one failing location at a time as a red
// dot with a pulse ring, its flag and its city. It fades in, stays about 2.4 s and
// is gone. The strip is its own row, so it never covers the words or the product
// stage. The markers are an illustration (the hero caption says so), not live data.
'use client'

import { useEffect, useRef, useState } from 'react'
import { LOCATIONS, type CheckLocation } from '@/lib/locations'
import { flagsDrawn } from './flag-support'
import {
  STRIP_INSET,
  STRIP_PAD_Y,
  WIDE_FROM,
  LABEL_W,
  LABEL_W_WIDE,
  countryCodeOf,
  labelSide,
  nextDelayMs,
  pickLocation,
  project,
  stripPoint,
} from './probe-blips-math'

const SHOW_MS = 2400

type Marker = { key: number; location: CheckLocation; x: number; y: number; side: 'left' | 'right'; wide: boolean }

// The dim dots use the same formula as `stripPoint`, in CSS, so they are right on
// the server render and after a resize without measuring the strip.
const dotStyle = (l: CheckLocation) => {
  const p = project(l.lat, l.lon)
  return {
    left: `calc(${STRIP_INSET}px + ${p.x} * (100% - ${2 * STRIP_INSET}px))`,
    top: `calc(${STRIP_PAD_Y}px + ${p.y} * (100% - ${2 * STRIP_PAD_Y}px))`,
  }
}

export function ProbeBlips({ rand = Math.random }: { rand?: () => number }) {
  const stripRef = useRef<HTMLDivElement>(null)
  const previous = useRef<string | null>(null)
  const [active, setActive] = useState(false)
  const [marker, setMarker] = useState<Marker | null>(null)
  const [flags, setFlags] = useState(true)

  // Run only while motion is allowed, the tab is visible and the strip is on screen.
  useEffect(() => {
    const el = stripRef.current
    if (!el) return
    const motion = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null
    let inView = true
    const sync = () => setActive(!motion?.matches && document.visibilityState !== 'hidden' && inView)
    const observer =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver((entries) => {
            inView = entries[entries.length - 1].isIntersecting
            sync()
          })
        : null
    observer?.observe(el)
    motion?.addEventListener('change', sync)
    document.addEventListener('visibilitychange', sync)
    setFlags(flagsDrawn())
    sync()
    return () => {
      observer?.disconnect()
      motion?.removeEventListener('change', sync)
      document.removeEventListener('visibilitychange', sync)
    }
  }, [])

  // One timer at a time: wait, show, wait. A width change hides the marker.
  useEffect(() => {
    const el = stripRef.current
    if (!active || !el) {
      setMarker(null)
      return
    }
    let timer: ReturnType<typeof setTimeout>
    let key = 0
    let shownWidth = 0
    const wait = () => {
      timer = setTimeout(show, nextDelayMs(rand))
    }
    const show = () => {
      const { width, height } = el.getBoundingClientRect()
      const location = pickLocation({ locations: LOCATIONS, previousCode: previous.current, rand })
      const { x, y } = stripPoint(location.lat, location.lon, width, height)
      const wide = width >= WIDE_FROM
      // The label widths are px at 16 px text; a larger text setting makes the label wider.
      const textScale = (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16) / 16
      previous.current = location.code
      shownWidth = width
      setMarker({ key: ++key, location, x, y, side: labelSide(x, width, (wide ? LABEL_W_WIDE : LABEL_W) * textScale), wide })
      timer = setTimeout(() => {
        setMarker(null)
        wait()
      }, SHOW_MS)
    }
    // Only a width change moves the label. A phone fires `resize` when the address bar hides on scroll, with the same width.
    const hide = () => {
      if (el.getBoundingClientRect().width !== shownWidth) setMarker(null)
    }
    window.addEventListener('resize', hide)
    wait()
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', hide)
    }
  }, [active, rand])

  return (
    <div ref={stripRef} data-probe-strip="" aria-hidden="true" className="pointer-events-none relative h-14">
      <span className="absolute inset-x-0 top-1/2 border-t border-dotted border-border" />
      {LOCATIONS.map((l) => (
        <span
          key={l.code}
          data-probe-dot={l.code}
          className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-body opacity-35"
          style={dotStyle(l)}
        />
      ))}
      {marker && <BlipMarker key={marker.key} marker={marker} flags={flags} />}
    </div>
  )
}

function BlipMarker({ marker, flags }: { marker: Marker; flags: boolean }) {
  const { location, x, y, side, wide } = marker
  return (
    <div
      data-probe-blip=""
      data-side={side}
      className="probe-blip absolute"
      style={{ left: `${x}px`, top: `${y}px` }}
    >
      <span className="probe-blip-ring absolute -left-2 -top-2 h-4 w-4 rounded-full border border-status-down" />
      <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-status-down" />
      <span
        className={`absolute top-1/2 flex -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-sm bg-card px-1.5 py-1 font-mono text-xs text-body ${
          side === 'right' ? 'left-3' : 'right-3'
        }`}
      >
        {flags ? (
          <span>{location.flag}</span>
        ) : (
          <span className="rounded-sm bg-muted px-1 text-muted-foreground">{countryCodeOf(location.flag)}</span>
        )}
        {wide && location.country !== location.city ? `${location.city}, ${location.country}` : location.city}
      </span>
    </div>
  )
}
